import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';

import { ReportTypeProps } from '../../../../../components/customer/report/types';
import { PrepopulatePermissions } from '../../../../../interfaces/PrepopulatePermissions';
import { DefaultFieldValues } from '../reportDialogs/DynamicReportDialog';

interface DefaultFieldsProps {
  reportTypeList: ReportTypeProps[];
  existingValues: DefaultFieldValues;
  valueRef: React.MutableRefObject<DefaultFieldValues>;
  permissions: PrepopulatePermissions;
  checkChanges: () => void;
}

const DefaultFields = ({
  reportTypeList,
  existingValues,
  permissions,
  checkChanges,
  valueRef,
}: DefaultFieldsProps) => {
  const fieldStyles = {
    height: '50px',
    marginTop: '12px',
    borderRadius: '6px',
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.0275)',
      border: '1px solid transparent',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
    '& .Mui-focused': {
      border: '1px solid black',
    },
  };

  const inputStyles = {
    height: '3.5rem',
    overflow: 'hidden',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    border: '',
    borderRadius: '6px',
  };

  const handleSeverityChange = (event: SelectChangeEvent) => {
    const newValues = { ...valueRef.current };
    newValues.priority = parseInt(event.target.value);
    valueRef.current = newValues;
    checkChanges();
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    const newValues = { ...valueRef.current };
    newValues.reportType = event.target.value;
    valueRef.current = newValues;
    checkChanges();
  };

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValues = { ...valueRef.current };
    newValues.assignee = event.target.value;
    valueRef.current = newValues;
    checkChanges();
  };

  const handleReferenceChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValues = { ...valueRef.current };
    newValues.referenceID = event.target.value;
    valueRef.current = newValues;
    checkChanges();
  };

  const handleDateChange = (value: Dayjs | null) => {
    const newDate = dayjs(value).format('YYYY-MM-DD');
    const newValues = { ...valueRef.current };
    newValues.dateReceived = newDate;
    valueRef.current = newValues;
    checkChanges();
  };

  return (
    <Grid container padding='24px 56px' spacing={1}>
      {permissions.caseManager && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            hiddenLabel
            variant='filled'
            placeholder='Case Manager Name'
            defaultValue={existingValues.assignee}
            disabled={permissions.caseManager}
            sx={fieldStyles}
            InputProps={{
              style: inputStyles,
              disableUnderline: true,
            }}
            onChange={handleNameChange}
          />
        </Grid>
      )}
      {permissions.dateReceived && (
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label='Date Received'
              disableFuture
              onChange={handleDateChange}
              disabled={permissions.dateReceived}
              defaultValue={dayjs(existingValues.dateReceived)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'filled',
                  sx: {
                    height: '50px',
                    marginTop: '12px',
                    borderRadius: '6px',
                    '& .MuiFilledInput-root:after': {
                      border: 'none',
                    },
                    '& .MuiFilledInput-root:before': {
                      borderBottom: 'none',
                    },
                    '& .MuiFilledInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.0275)',
                      border: '1px solid transparent',
                    },
                    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                      {
                        borderBottom: 'none',
                      },
                    '& .MuiFilledInput-root.Mui-focused': {
                      border: '1px solid black',
                    },
                    '& .MuiFilledInput-root.Mui-error': {
                      border: '1px solid red',
                    },
                  },
                  size: 'small',
                  InputProps: {
                    disableUnderline: true,
                    style: {
                      borderRadius: '6px',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      )}
      {permissions.reportType && (
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth hiddenLabel>
            <Select
              variant='filled'
              disableUnderline
              displayEmpty
              sx={fieldStyles}
              onChange={handleTypeChange}
              disabled={permissions.reportType}
              defaultValue={existingValues.reportType}
            >
              <MenuItem disabled value={''}>
                Report Type
              </MenuItem>
              {reportTypeList.map((report) => (
                <MenuItem value={report.name} key={report.id}>
                  {report.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      {permissions.reportReference && (
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            hiddenLabel
            variant='filled'
            placeholder='Report Reference'
            defaultValue={existingValues.referenceID}
            disabled={permissions.reportReference}
            sx={fieldStyles}
            InputProps={{
              style: inputStyles,
              disableUnderline: true,
            }}
            onChange={handleReferenceChange}
          />
        </Grid>
      )}
      {permissions.reportSeverity && (
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth hiddenLabel>
            <Select
              variant='filled'
              disableUnderline
              displayEmpty
              sx={fieldStyles}
              defaultValue={existingValues.priority.toString()}
              disabled={permissions.reportSeverity}
              onChange={handleSeverityChange}
            >
              <MenuItem disabled value={'-1'}>
                Report Severity
              </MenuItem>
              <MenuItem value={'0'}>Low</MenuItem>
              <MenuItem value={'1'}>Medium</MenuItem>
              <MenuItem value={'2'}>High</MenuItem>
              <MenuItem value={'3'}>Critical</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

export default DefaultFields;
